import { Component, Input, Optional } from '@angular/core';
import { ContractRates, RailroadRegularTimeBasis } from 'src/shared/api';
import { FormConfig, CustomFormAccessor } from '../form-cva.component';
import {
	NgControl,
	FormBuilder,
	Validators,
	//FormGroup
} from '@angular/forms';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import { takeUntil, map } from 'rxjs/operators';
import { MatDialogRef } from '@angular/material/dialog';
import { ContractRatesDialogComponent } from './contract-rates-dialog/contract-rates-dialog.component';
import { SERVICE_TYPE } from 'src/shared/constants';
import { BehaviorSubject } from 'rxjs';

const maxHoursInWeek = 168;
const minHoursInWeek = 1;

const billingAmountValidator = [
	CustomValidators.mustBeNumeric,
	Validators.min(0),
];

const maxHoursInWeekValidator = [
	Validators.max(maxHoursInWeek),
	Validators.min(minHoursInWeek),
];

export const contractRatesFormConfig: FormConfig<ContractRates> = {
	id: [''],
	standardDailyRate: ['', [...billingAmountValidator]],
	overtimeHourlyRate: ['', billingAmountValidator],
	nightDailyRate: ['', billingAmountValidator],
	weekendDailyRate: ['', billingAmountValidator],
	holidayDailyRate: ['', billingAmountValidator],
	nightOvertimeHourlyRate: ['', billingAmountValidator],
	weekendOvertimeHourlyRate: ['', billingAmountValidator],
	holidayOvertimeHourlyRate: ['', billingAmountValidator],
	initialMobilization: ['', billingAmountValidator],
	remobilizationFee: ['', billingAmountValidator],
	demobilization: ['', billingAmountValidator],
	dailyMileageFee: ['', billingAmountValidator],
	billActualMileage: [''],
	expeditedServiceFee: ['', billingAmountValidator],
	processingAndHandlingFee: ['', billingAmountValidator],
	certifiedPayrollFee: ['', [...billingAmountValidator]],
	isProcessingFeeMonthly: [''],
	isPayrollFeeMonthly: [''],
	isPayrollFeeWeekly: [''],
	standardDayLengthHours: ['', [Validators.max(24), Validators.min(1)]],
	standardHourlyRate: ['', [...billingAmountValidator]],
	standardMonthlyRate: ['', [...billingAmountValidator]],
	billActualHours: [''],
	weeklyOvertimeThreshold: ['', maxHoursInWeekValidator],
	perDiem: ['', billingAmountValidator],
	serviceType: [''],
	notes: [''],
	standDownDayRate: ['', billingAmountValidator],
	railroadAdministrationFee: ['', billingAmountValidator],
};

@Component({
	selector: 'rpshared-contract-rates',
	templateUrl: './contract-rates.component.html',
})
export class ContractRatesComponent extends CustomFormAccessor<
	ContractRates | ContractRates[]
> {
	isRequiredPayrollFee$ = new BehaviorSubject<boolean>(false);
	@Input()
	set isTurnOnPayrollFeeWeekly(value: boolean) {
		if (value) {
			this.form.get('isPayrollFeeWeekly').setValue(true);
			this.isRequiredPayrollFee$.next(true);
		} else {
			this.isRequiredPayrollFee$.next(false);
		}
	}
	@Input() regularTimeBasis: RailroadRegularTimeBasis;
	@Input() isDayLengthRequired = true;

	// ISO 4217 code the rates are denominated in; derived from the job's country by the host.
	@Input() currencyCode = 'USD';

	regularTimeBasisEnum = RailroadRegularTimeBasis;

	utilityServiceType = SERVICE_TYPE.Monitoring;

	constructor(
		public ngControl: NgControl,
		public fb: FormBuilder,
		@Optional()
		public dialogRef: MatDialogRef<ContractRatesDialogComponent>
	) {
		super(ngControl, fb, contractRatesFormConfig);
	}
}

@Component({
	selector: 'rpshared-contract-rates-as-single-item-array',
	templateUrl: './contract-rates.component.html',
})
export class ContractRatesAsSingleItemArrayComponent extends ContractRatesComponent {
	@Input() regularTimeBasis: RailroadRegularTimeBasis;

	regularTimeBasisEnum = RailroadRegularTimeBasis;

	// model --> view
	writeValue(value: ContractRates[]): void {
		if (value) {
			const contractedRates = value.find((v) => v && v.serviceType);
			this.form.patchValue(contractedRates || {}, { emitEvent: false });
			this.form.markAsUntouched();
		} else {
			this.form.reset();
		}
	}

	// view --> model
	registerOnChange(fn: (value: ContractRates[]) => void) {
		this.form.valueChanges
			.pipe(
				map((rates) => [rates]),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
}
